import dayjs from 'dayjs';
import { CmsProduct, CmsVirtualProduct, SeasonSelectionOption } from '../../../../types/cms_shared_types';
import { Product } from '../../../../types/webshop';
import {
  MusicClass,
  ProductConfigurationInputs,
  ProductConfigurationModalFormValues,
} from './ProductConfigurationModal';

export const getInitialValues = (
  cmsProduct: CmsProduct,
  selectedProduct?: Product,
  virtualProduct?: CmsVirtualProduct,
  selectedProducts?: Product[],
): ProductConfigurationModalFormValues => {
  const baseValues = getProductInitialValues(cmsProduct, selectedProduct);

  if (!virtualProduct) {
    return baseValues;
  }

  const productValues = Object.fromEntries(
    virtualProduct.products.map((product) => {
      const selectedVirtualProduct = selectedProducts?.find((selected) => selected.code === product.code);
      return [product.code, getProductInitialValues(product, selectedVirtualProduct)];
    }),
  );

  return {
    ...baseValues,
    productValues,
  };
};

const getProductInitialValues = (cmsProduct: CmsProduct, selectedProduct?: Product): ProductConfigurationInputs => {
  const seasonSelectionEnabled =
    cmsProduct.seasonSelection !== SeasonSelectionOption.DISABLED &&
    !!(cmsProduct.startDatePrompt && cmsProduct.endDatePrompt);

  const isSeasonallyOpen = seasonSelectionEnabled
    ? selectedProduct
      ? selectedProduct.startDate !== undefined && selectedProduct.endDate !== undefined
      : cmsProduct.seasonSelection === SeasonSelectionOption.MANDATORY
    : false;

  const isCustomMeasureInput =
    (selectedProduct &&
      cmsProduct.measureOptions.length > 0 &&
      cmsProduct.measureOptions.every((o) => o.salesforceValue.toString() !== selectedProduct.quantity)) ??
    false;

  const isCustomTimeMeasureInput =
    (selectedProduct &&
      cmsProduct.timeMeasureOptions.length > 0 &&
      cmsProduct.timeMeasureOptions.every((o) => o.salesforceValue.toString() !== selectedProduct.timeQuantity)) ??
    false;

  const initialValues = {
    measureInput: getInitialMeasure(cmsProduct, selectedProduct, isCustomMeasureInput),
    customMeasureInput: getInitialCustomMeasure(cmsProduct, selectedProduct, isCustomMeasureInput),
    customMeasureInputSelected: isCustomMeasureInput,
    timeMeasureInput: getInitialTimeMeasure(cmsProduct, selectedProduct, isCustomMeasureInput, seasonSelectionEnabled),
    musicClassToggleInput: getInitialMusicClassToggle(selectedProduct),
    customTimeMeasureInputSelected: getInitialCustomTimeMeasureSelected(
      cmsProduct,
      selectedProduct,
      isCustomTimeMeasureInput,
      seasonSelectionEnabled,
    ),
    customTimeMeasureInput: getInitialCustomTimeMeasure(selectedProduct, isCustomTimeMeasureInput),
    startDate: getInitialStartDate(selectedProduct),
    endDate: getInitialEndDate(selectedProduct),
    _timeMeasureInputSelection: getInitialTimeMeasureSelection(
      cmsProduct,
      selectedProduct,
      isCustomTimeMeasureInput,
      seasonSelectionEnabled,
    ),
    isSeasonallyOpen: isSeasonallyOpen,
  };

  return initialValues;
};

const getInitialMeasure = (
  cmsProduct: CmsProduct,
  selectedProduct: Product | undefined,
  isCustomMeasureInput: boolean,
): string => {
  if (!selectedProduct) {
    return '';
  }

  if (cmsProduct.isSchablon) {
    return selectedProduct.schablonQuantity ?? '';
  }

  return isCustomMeasureInput ? cmsProduct.measureOptionsLastOptionName : selectedProduct.quantity;
};

const getInitialCustomMeasure = (
  cmsProduct: CmsProduct,
  selectedProduct: Product | undefined,
  isCustomMeasureInput: boolean,
): string => {
  if (!selectedProduct) {
    return '';
  }

  if (cmsProduct.isSchablon) {
    return '';
  }

  if (isCustomMeasureInput) {
    return selectedProduct.quantity;
  }

  return '';
};

const getInitialTimeMeasure = (
  cmsProduct: CmsProduct,
  selectedProduct: Product | undefined,
  isCustomTimeMeasureInput: boolean,
  seasonSelectionEnabled: boolean,
): string => {
  if (!selectedProduct) {
    return '';
  }

  if (cmsProduct.isSchablon) {
    return selectedProduct.schablonTimeQuantity ?? '';
  }

  if (seasonSelectionEnabled) {
    return selectedProduct.timeQuantity;
  }

  return isCustomTimeMeasureInput ? cmsProduct.timeMeasureOptionsLastOptionName : selectedProduct.timeQuantity;
};

const getInitialMusicClassToggle = (product: Product | undefined): boolean => {
  if (!product) {
    return false;
  }

  return product.musicClass === MusicClass.B;
};

const getInitialCustomTimeMeasureSelected = (
  cmsProduct: CmsProduct,
  selectedProduct: Product | undefined,
  isCustomTimeMeasureInput: boolean,
  seasonSelectionEnabled: boolean,
): boolean => {
  if (!selectedProduct) {
    return false;
  }

  if (cmsProduct.isSchablon) {
    return false;
  }

  if (!seasonSelectionEnabled) {
    return isCustomTimeMeasureInput;
  }

  return false;
};

const getInitialCustomTimeMeasure = (product: Product | undefined, isCustomTimeMeasureInput: boolean): string => {
  if (!product || !isCustomTimeMeasureInput) {
    return '';
  }

  return product.timeQuantity;
};

const getInitialStartDate = (product: Product | undefined): string | undefined => {
  if (!product || !product.startDate) {
    return dayjs().startOf('year').toString();
  }

  return product.startDate;
};

const getInitialEndDate = (product: Product | undefined): string | undefined => {
  if (!product || !product.endDate) {
    return dayjs().endOf('year').toString();
  }

  return product.endDate;
};

const getInitialTimeMeasureSelection = (
  cmsProduct: CmsProduct,
  selectedProduct: Product | undefined,
  isCustomTimeMeasureInput: boolean,
  seasonSelectionEnabled: boolean,
): string => {
  if (!selectedProduct) {
    return '';
  }

  if (cmsProduct.isSchablon) {
    return selectedProduct._timeQuantitySelection ?? selectedProduct.schablonTimeQuantity ?? '';
  }

  if (seasonSelectionEnabled) {
    return selectedProduct._timeQuantitySelection ?? selectedProduct.timeQuantity;
  }

  return (
    selectedProduct._timeQuantitySelection ??
    (isCustomTimeMeasureInput ? cmsProduct.timeMeasureOptionsLastOptionName : selectedProduct.timeQuantity)
  );
};
